import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  FolderOpen,
  Tag,
  Calendar,
  Users,
  Settings
} from 'lucide-react';
import { EventCategory, CategoryFormData } from '../../types/event';
import categoryAPI from '../../services/categoryAPI';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import EnhancedLightCard from '../../components/UI/EnhancedLightCard';
import { useAppSelector } from '../../store/hooks';
import { useAlert } from '../../contexts';
import { logError } from '../../utils/errorLogger';

const CategoriesPageSimple: React.FC = () => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { theme } = useAppSelector((state) => state.ui);
  const { showConfirm } = useAlert();
  
  // State - ensure categories is always an array
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<EventCategory | null>(null);

  // Form state - simplified to match backend
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    name_ar: '',
  });

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const categoriesData = await categoryAPI.getCategories();
      
      // Ensure we always have an array
      if (Array.isArray(categoriesData)) {
        setCategories(categoriesData);
      } else {
        if (process.env.NODE_ENV === 'development') {
          // Debug: Categories data is not an array
        }
        setCategories([]);
        setError('Invalid categories data format received');
      }
    } catch (err: any) {
      logError('Failed to fetch categories', err);
      setCategories([]); // Ensure categories is always an array
      setError(err.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user is authenticated
    // Note: tokens are in httpOnly cookies, not in Redux state
    if (isAuthenticated) {
      fetchCategories();
    } else {
      if (process.env.NODE_ENV === 'development') {
        // Debug: User not authenticated yet, waiting...
      }
      setLoading(false);
      setError('Please log in to view categories');
    }
  }, [isAuthenticated]);

  // Filter categories by search term - simplified with safety check
  const filteredCategories = Array.isArray(categories) 
    ? categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (category.name_ar || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingCategory) {
        await categoryAPI.updateCategory(editingCategory.id, formData);
      } else {
        await categoryAPI.createCategory(formData);
      }

      // Reset form and refresh data
      setFormData({ name: '', name_ar: '' });
      setShowCreateForm(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete - only for admins
  const handleDelete = async (categoryId: number) => {
    const confirmed = await showConfirm('Are you sure you want to delete this category?');
    if (!confirmed) {
      return;
    }

    try {
      await categoryAPI.deleteCategory(categoryId);
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  // Start editing
  const startEdit = (category: EventCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      name_ar: category.name_ar || '',
    });
    setShowCreateForm(true);
  };

  // Cancel editing
  const cancelEdit = () => {
    setShowCreateForm(false);
    setEditingCategory(null);
    setFormData({ name: '', name_ar: '' });
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Event Categories
            </h1>
            <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {isAdmin ? 'Manage global event categories' : 'View available event categories'}
            </p>
          </div>
          {isAdmin && (
            <div className="mt-4 sm:mt-0">
              <Button 
                onClick={() => setShowCreateForm(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Create Category
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards — aligned with dashboard style */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
        <EnhancedLightCard
          title="Total Categories"
          value={categories.length}
          icon={<Tag />}
          compactHorizontal
        />
        <EnhancedLightCard
          title="Active Categories"
          value={categories.filter(cat => (cat.events_count || 0) > 0).length}
          icon={<Calendar />}
          compactHorizontal
        />
      </div>

      {/* Content Cards */}
      <div className="grid grid-cols-1 gap-6">
        {/* Search and Filters */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
              Search Categories
            </h3>
          </div>
          <div className="p-6">
            <div className="max-w-md">
              <Input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`border rounded-md p-4 ${
            theme === 'dark' 
              ? 'bg-red-900/30 border-red-700/50' 
              : 'bg-red-50 border-red-200'
          }`}>
            <p className={theme === 'dark' ? 'text-red-300' : 'text-red-800'}>{error}</p>
          </div>
        )}

        {/* Create/Edit Form - Only for admins */}
        {showCreateForm && isAdmin && (
          <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-black/80 border-gray-800/50' 
              : 'bg-white/90 border-gray-200/50'
          }`}>
            <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Category name (English)"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Wedding, Graduation"
                />
                <Input
                  label="Category name (Arabic)"
                  type="text"
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required={!editingCategory}
                  placeholder="e.g., زفاف، تخرج"
                  dir="rtl"
                />
                {editingCategory && (
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Leave Arabic empty only temporarily; Arabic invites fall back to the English name.
                  </p>
                )}
                <div className="flex gap-3">
                  <Button type="submit" loading={loading}>
                    {editingCategory ? 'Update Category' : 'Create Category'}
                  </Button>
                  <Button variant="outline" onClick={cancelEdit}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
          theme === 'dark' 
            ? 'bg-black/80 border-gray-800/50' 
            : 'bg-white/90 border-gray-200/50'
        }`}>
          {loading && !showCreateForm ? (
            <div className="p-8 text-center">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${
                theme === 'dark' ? 'border-[#33FF00]' : 'border-mirsal-green'
              }`}></div>
              <p className={`mt-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Loading categories...
              </p>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="p-8 text-center">
              <FolderOpen className={`h-12 w-12 mx-auto mb-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <h3 className={`text-lg font-medium mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                No categories found
              </h3>
              <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                {searchTerm ? 'No categories match your search.' : 'No categories available yet.'}
              </p>
              {!searchTerm && isAdmin && (
                <Button onClick={() => setShowCreateForm(true)}>
                  Create First Category
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden">
              <div className={`px-6 py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Categories ({filteredCategories.length})
                </h3>
              </div>
              <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredCategories.map((category) => (
                  <div key={category.id} className={`px-6 py-4 ${
                    theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {category.name}
                        </h4>
                        {category.name_ar ? (
                          <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} dir="rtl">
                            {category.name_ar}
                          </p>
                        ) : (
                          <p className={`text-xs mt-0.5 italic ${theme === 'dark' ? 'text-amber-400/90' : 'text-amber-700'}`}>
                            Arabic name not set
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          ID: {category.id} • Events: {category.events_count || 0}
                        </p>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(category)}
                            leftIcon={<Edit className="h-3 w-3" />}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(category.id)}
                            leftIcon={<Trash2 className="h-3 w-3" />}
                            className={theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}
                          >
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesPageSimple;
